/**
 * Suggested patches (MB, decisions 55, 155 and 194, Q28, Q170; spec overview.md §4.2.1 "Suggested patches").
 *
 * Diurnal QC (the exceedance workbook rows) and Geosoft QC (a line's failed contract tests) point at a bad section
 * of a flown line. Teslyn turns it into a suggestion the reviewer confirms in Line review; a suggestion never
 * creates a planned line by itself.
 *
 * MB's rules:
 * - the patch reaches past at least two tie lines beyond each end of the bad section, then a further 200 m
 *   (decision 155 a); with fewer than two tie lines left before the end of the line, it runs to that end;
 * - a bad section in the middle of a line gets that overlap at both ends (decision 29 / 155);
 * - a full reflight is suggested instead when the patch would cover more than half of a line shorter than 20 km
 *   (decision 155 b). Claude's reading, recorded in the decision: a line of 20 km or longer always gets a patch.
 *
 * - a tie line's patch follows its own rule (decision 194): it overlaps the accepted part of the line by at least
 *   the tie-line spacing plus 200 m beyond each end of the bad section (ties 1 km apart: at least 1,200 m), never
 *   past the line's ends. The spacing is the distance to the nearest parallel tie line of the block. A tie with no
 *   parallel tie to measure from takes the traverse rule, with the crossing traverses as its "tie lines".
 *
 * Several bad sections of one flown line give one patch spanning all of them (as `defaultPatchSegment` does for
 * two uncovered ends).
 *
 * Positions along the line: a QC result gives times (diurnal) or fiducials (Geosoft); the share of the logged run
 * they mark is placed on the planned line between where the log says the line started and ended
 * (`start_along_m`, `end_along_m`). Without logged coordinates the run is taken from the end the heading points
 * away from, as for the default patch segment.
 */
import { flightNumberOfTag, type DiurnalLine } from './basemag.js';
import { addIsoDays, isoDate } from './dates.js';
import type { WorkbookRow } from './diurnal-exports.js';
import { qcEventText, type QcLineResult } from './geosoft-qc.js';
import { parseLineNumber } from './lines.js';
import type { Flight, LineStatus, PlannedLine, Uuid } from './model.js';
import { fidAtAlong, patchRow, type FidRun, type LnsRow } from './lns-files.js';
import { defaultPatchSegment, plannedLineRole, type Segment } from './reflights.js';

/** Tie lines the patch must reach past beyond each end of the bad section (decision 155 a). */
export const PATCH_TIE_OVERLAP = 2;
/** Metres added after the last of those tie lines (decision 155 a). */
export const PATCH_EXTRA_M = 200;
/** Lines shorter than this get a full reflight when the patch would cover more than half (decision 155 b). */
export const FULL_REFLIGHT_LINE_MAX_M = 20_000;
export const FULL_REFLIGHT_SHARE = 0.5;
/** Line review's reason limit. */
const REASON_MAX = 100;

const cm = (m: number): number => Math.round(m * 100) / 100;
const km = (m: number): string => (m / 1000).toFixed(2);

/** Where a bad section comes from: Diurnal QC, Flight data QC, or the processor's refly flag (R127). */
export type BadSectionSource = 'diurnal' | 'geosoft' | 'processor';

/** A stretch of a flown line that QC marks as out of specification. */
export interface BadSection {
  readonly source: BadSectionSource;
  /** Metres along the original line from its first point. */
  readonly from_m: number;
  readonly to_m: number;
  /** What QC said, in plain words ("Diurnal Gradient 16:13:19 to 16:17:57"). */
  readonly label: string;
  /**
   * Local times (`HH:MM:SS`) at `from_m` and `to_m`, where QC knows them (Diurnal QC: placed along the line's logged
   * time); for the master refly list's own row per section (decision 543, 3B).
   */
  readonly times?: { readonly from: string | null; readonly to: string | null };
  /** ZFIDs at `from_m` and `to_m` (Geosoft QC: placed along the flown run). */
  readonly zfids?: { readonly from: number | null; readonly to: number | null };
  /** The Geosoft QC test that failed (`terrain_clearance`, ...); for a refly flag, the test it was picked from. */
  readonly test_key?: string;
  /** A refly flag's own words (R127), for the Refly list's reason word. */
  readonly reason_text?: string;
}

export interface PatchSuggestion {
  readonly line_status_id: Uuid;
  readonly line_number: number;
  readonly original_line_id: Uuid;
  readonly original_number: number;
  readonly line_length_m: number;
  readonly kind: 'patch' | 'reflight';
  /** The bad sections, merged into one span. */
  readonly bad: Segment;
  /** The patch MB's rule gives (also when a full reflight is suggested instead, to show why). */
  readonly patch: Segment;
  /** Where the crossing lines the patch reaches past lie (metres along the line). */
  readonly crossings_used: readonly number[];
  readonly sections: readonly BadSection[];
  /** Accepted metres for a partial review with this patch: the line outside it, at most the flown length. */
  readonly accepted_m: number;
  /** For Line review's reason field (at most 100 characters). */
  readonly reason: string;
  /** The rule as applied, for the screen. */
  readonly explanation: string;
}

// ------------------------------------------------------------------------------------------------- geometry

function segLength(l: PlannedLine): number {
  return Math.hypot(l.x2 - l.x1, l.y2 - l.y1);
}

/** Metres along `line` (from x1, y1) where `other` crosses it, or null when the two segments do not meet. */
export function crossingAlong(line: PlannedLine, other: PlannedLine): number | null {
  const dx = line.x2 - line.x1;
  const dy = line.y2 - line.y1;
  const ex = other.x2 - other.x1;
  const ey = other.y2 - other.y1;
  const den = dx * ey - dy * ex;
  if (Math.abs(den) < 1e-9) return null;
  const qx = other.x1 - line.x1;
  const qy = other.y1 - line.y1;
  const t = (qx * ey - qy * ex) / den;
  const u = (qx * dy - qy * dx) / den;
  if (t < 0 || t > 1 || u < 0 || u > 1) return null;
  return cm(t * segLength(line));
}

/**
 * Where the lines of the other direction cross `original` (ties across a traverse, traverses across a tie), in
 * metres along it, ascending. Only original lines of the same block, so a reflight or patch never counts twice.
 */
export function crossingsAlong(original: PlannedLine, plan: readonly PlannedLine[]): number[] {
  const out: number[] = [];
  for (const p of plan) {
    if (p.deleted_at !== null || p.id === original.id || p.block_id !== original.block_id) continue;
    if (p.kind === original.kind || plannedLineRole(p) !== 'original') continue;
    const at = crossingAlong(original, p);
    if (at !== null) out.push(at);
  }
  return out.sort((a, b) => a - b);
}

/**
 * The patch over `bad` on a line of `length` metres with crossing lines at `crossings` (ascending): past at least
 * `PATCH_TIE_OVERLAP` crossings beyond each end, then `PATCH_EXTRA_M`, never beyond the line's ends.
 */
export function patchForBadSection(
  bad: Segment,
  length: number,
  crossings: readonly number[],
): { readonly patch: Segment; readonly crossings_used: number[] } {
  const before = crossings.filter((c) => c < bad.from_m).sort((a, b) => b - a);
  const after = crossings.filter((c) => c > bad.to_m).sort((a, b) => a - b);
  const used: number[] = [];
  let from = 0;
  if (before.length >= PATCH_TIE_OVERLAP) {
    const c = before[PATCH_TIE_OVERLAP - 1]!;
    used.push(...before.slice(0, PATCH_TIE_OVERLAP));
    from = Math.max(0, c - PATCH_EXTRA_M);
  }
  let to = length;
  if (after.length >= PATCH_TIE_OVERLAP) {
    const c = after[PATCH_TIE_OVERLAP - 1]!;
    used.push(...after.slice(0, PATCH_TIE_OVERLAP));
    to = Math.min(length, c + PATCH_EXTRA_M);
  }
  return { patch: { from_m: cm(from), to_m: cm(to) }, crossings_used: used.sort((a, b) => a - b) };
}

/** Lines within this angle (degrees) of each other count as parallel when measuring the tie spacing. */
const PARALLEL_DEG = 10;

/**
 * The tie-line spacing at `tie` (decision 194): the distance from its mid-point to the nearest other original tie
 * line of the block that runs parallel to it (within 10°), in metres; null when there is none.
 */
export function tieSpacingM(tie: PlannedLine, plan: readonly PlannedLine[]): number | null {
  const mx = (tie.x1 + tie.x2) / 2;
  const my = (tie.y1 + tie.y2) / 2;
  const dir = Math.atan2(tie.y2 - tie.y1, tie.x2 - tie.x1);
  let best: number | null = null;
  for (const p of plan) {
    if (p.deleted_at !== null || p.id === tie.id || p.block_id !== tie.block_id || p.kind !== 'tie') continue;
    if (plannedLineRole(p) !== 'original') continue;
    const len = segLength(p);
    if (len === 0) continue;
    const a = Math.atan2(p.y2 - p.y1, p.x2 - p.x1);
    const diff = Math.abs((((a - dir) * 180) / Math.PI + 360) % 180);
    if (Math.min(diff, 180 - diff) > PARALLEL_DEG) continue;
    const d = Math.abs((mx - p.x1) * (p.y2 - p.y1) - (my - p.y1) * (p.x2 - p.x1)) / len;
    if (d > 1 && (best === null || d < best)) best = d;
  }
  return best === null ? null : cm(best);
}

/** The tie-line patch over `bad` (decision 194): `spacing + 200 m` beyond each end, within the line. */
export function tiePatchForBadSection(bad: Segment, length: number, spacingM: number): Segment {
  const reach = spacingM + PATCH_EXTRA_M;
  return { from_m: cm(Math.max(0, bad.from_m - reach)), to_m: cm(Math.min(length, bad.to_m + reach)) };
}

/** Heading of `line` from (x1, y1) to (x2, y2), degrees clockwise from grid north. */
function bearing(line: PlannedLine): number {
  return ((Math.atan2(line.x2 - line.x1, line.y2 - line.y1) * 180) / Math.PI + 360) % 360;
}

/** Where the logged run of `status` started and ended on `original` (metres along it). */
export function loggedRun(
  status: Pick<LineStatus, 'flown_m' | 'direction_deg' | 'start_along_m' | 'end_along_m'>,
  original: PlannedLine,
): { readonly start_m: number; readonly end_m: number } {
  if (status.start_along_m != null && status.end_along_m != null)
    return { start_m: status.start_along_m, end_m: status.end_along_m };
  const length = original.length_m;
  let forward = true;
  if (status.direction_deg != null) {
    const gap = Math.abs(((status.direction_deg - bearing(original) + 540) % 360) - 180);
    forward = gap <= 90;
  }
  const run = Math.min(status.flown_m, length);
  return forward ? { start_m: 0, end_m: run } : { start_m: length, end_m: length - run };
}

/** The stretch of the line between two shares (0 at the logged start, 1 at the logged end) of the run. */
function sectionOfRun(
  run: { readonly start_m: number; readonly end_m: number },
  a: number,
  b: number,
  length: number,
): Segment {
  const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));
  const p = run.start_m + clamp01(a) * (run.end_m - run.start_m);
  const q = run.start_m + clamp01(b) * (run.end_m - run.start_m);
  const lo = Math.max(0, Math.min(p, q));
  const hi = Math.min(length, Math.max(p, q));
  return { from_m: cm(lo), to_m: cm(hi) };
}

// ------------------------------------------------------------------------------------------------- suggestion

function originalOf(status: LineStatus, plan: readonly PlannedLine[]): PlannedLine | undefined {
  const parsed = parseLineNumber(status.line_number);
  if (!parsed.production) return undefined;
  return plan.find(
    (p) => p.deleted_at === null && p.number === parsed.base && plannedLineRole(p) === 'original',
  );
}

const fitReason = (text: string): string =>
  text.length <= REASON_MAX ? text : `${text.slice(0, REASON_MAX - 1)}…`;

/**
 * MB's suggestion for a flown line with bad sections (decision 155), or null when there are none or the line has no
 * original in the plan.
 */
export function suggestPatch(
  status: LineStatus,
  sections: readonly BadSection[],
  plan: readonly PlannedLine[],
): PatchSuggestion | null {
  const original = originalOf(status, plan);
  if (!original || sections.length === 0) return null;
  const length = original.length_m;
  const bad: Segment = {
    from_m: cm(Math.max(0, Math.min(...sections.map((s) => s.from_m)))),
    to_m: cm(Math.min(length, Math.max(...sections.map((s) => s.to_m)))),
  };
  if (!(bad.to_m > bad.from_m)) return null;
  const spacing = original.kind === 'tie' ? tieSpacingM(original, plan) : null;
  const { patch, crossings_used } =
    spacing !== null
      ? { patch: tiePatchForBadSection(bad, length, spacing), crossings_used: [] as number[] }
      : patchForBadSection(bad, length, crossingsAlong(original, plan));
  const fmtM = (m: number): string => Math.round(m).toLocaleString('en-AU');
  const rule =
    spacing !== null
      ? `overlaps the accepted line by the tie spacing ${fmtM(spacing)} m plus ${PATCH_EXTRA_M} m at each end`
      : `reaches past ${
          crossings_used.length === 2 * PATCH_TIE_OVERLAP
            ? `${PATCH_TIE_OVERLAP} tie lines at each end`
            : `${PATCH_TIE_OVERLAP} tie lines where the line allows (to the line's end otherwise)`
        } plus ${PATCH_EXTRA_M} m`;
  const patchM = patch.to_m - patch.from_m;
  const share = length > 0 ? patchM / length : 1;
  const reflight = length < FULL_REFLIGHT_LINE_MAX_M && share > FULL_REFLIGHT_SHARE;
  const SOURCE_WORDS: Readonly<Record<BadSectionSource, string>> = {
    diurnal: 'diurnal',
    geosoft: 'flight data QC',
    processor: 'flagged',
  };
  const sources = [...new Set(sections.map((s) => SOURCE_WORDS[s.source]))].join(' and ');
  const pct = Math.round(share * 100);
  const lineKm = `${km(length)} km`;
  const explanation = reflight
    ? `A patch that ${rule} would cover ${pct} % of this ${lineKm} line (more than half of a line under ${FULL_REFLIGHT_LINE_MAX_M / 1000} km), so a full reflight is suggested.`
    : `Bad section ${km(bad.from_m)} to ${km(bad.to_m)} km along the line; the patch ${rule}: ${km(patch.from_m)} to ${km(patch.to_m)} km, ${pct} % of the ${lineKm} line.`;
  const reason = reflight
    ? fitReason(`Suggested reflight (${sources}): a patch would cover ${pct} % of the line`)
    : fitReason(`Suggested patch ${km(patch.from_m)}-${km(patch.to_m)} km (${sources} out of spec)`);
  return {
    line_status_id: status.id,
    line_number: status.line_number,
    original_line_id: original.id,
    original_number: original.number,
    line_length_m: length,
    kind: reflight ? 'reflight' : 'patch',
    bad,
    patch,
    crossings_used,
    sections,
    accepted_m: reflight ? 0 : cm(Math.max(0, Math.min(status.flown_m, length - patchM))),
    reason,
    explanation,
  };
}

// ------------------------------------------------------------------------------------------------- partial (#69)

/** Where the patch of a part-accepted line came from. */
export type PartialPatchSource = 'gap' | 'bad_sections';

export interface PartialPatch {
  readonly segment: Segment;
  readonly source: PartialPatchSource;
  /**
   * With `bad_sections`: the suggestion MB's rule gave. Its `kind` is `reflight` when the patch would cover more than
   * half of a line shorter than 20 km (decision 155 b): the segment is then the whole line.
   */
  readonly suggestion: PatchSuggestion | null;
}

/**
 * The patch a part-accepted line calls for when the reviewer types no ends (issue #69, MB 27 Oct 2026: lines 102730
 * and 190350 of F26048, flown past both ends and part-accepted for a terrain clearance bust in the middle, got
 * full-length "patches"):
 * - the part of the line the accepted data does not cover (`defaultPatchSegment`), when there is one;
 * - else, when the accepted data covers the whole line, the patch MB's rule gives over the line's bad sections
 *   (`suggestPatch`: the same rule as Suggested patches, Flight data QC fails and Diurnal QC's kept sections);
 * - null when there is neither: Line review then asks for the ends (or Reject for a full reflight) and the Refly list
 *   says so on the row. A full reflight is never made silently.
 */
export function partialPatch(
  status: LineStatus,
  accepted_m: number,
  original: PlannedLine,
  plan: readonly PlannedLine[],
  sections: readonly BadSection[],
): PartialPatch | null {
  const gap = defaultPatchSegment({ ...status, accepted_m }, original);
  if (gap) return { segment: gap, source: 'gap', suggestion: null };
  return sectionsPatch(status, original, plan, sections);
}

/**
 * The patch MB's rule gives over the given bad sections alone (`suggestPatch`), never the gap in the accepted data:
 * the whole line when the rule says reflight, null when there is no section. A line with the processor's refly flags
 * (R127) is patched over its flags only, so its flags go here instead of `partialPatch`.
 */
export function sectionsPatch(
  status: LineStatus,
  original: PlannedLine,
  plan: readonly PlannedLine[],
  sections: readonly BadSection[],
): PartialPatch | null {
  const suggestion = suggestPatch(status, sections, plan);
  if (!suggestion) return null;
  const segment: Segment =
    suggestion.kind === 'patch' ? suggestion.patch : { from_m: 0, to_m: cm(original.length_m) };
  return { segment, source: 'bad_sections', suggestion };
}

const wholeShare = (whole: PatchSuggestion): string =>
  `${Math.round(((whole.patch.to_m - whole.patch.from_m) / whole.line_length_m) * 100)} % of this ${km(
    whole.line_length_m,
  )} km line (more than half of a line under ${FULL_REFLIGHT_LINE_MAX_M / 1000} km)`;

/**
 * Why a part-accepted line's refly is the whole line, for the Refly list and the plan preview (issue #69): `whole`
 * is the rule's own answer (a patch would cover more than half of a short line), null is no patch at all.
 */
export function partialWithoutPatchWords(lineNumber: number, whole: PatchSuggestion | null): string {
  return whole
    ? `Line ${lineNumber} was part-accepted, but a patch over its bad sections would cover ${wholeShare(whole)}, so its refly is the whole line. Reject it in Line review for a full reflight, or set the patch ends there with Partial.`
    : `Line ${lineNumber} was part-accepted, but no patch could be made: the accepted data covers the whole line and QC found no bad section, so its refly is the whole line. Set the patch ends in Line review with Partial, or Reject it for a full reflight.`;
}

/** Why Line review's "Partial" needs the patch ends typed for this line (issue #69), or Reject. */
export function partialNeedsEndsWords(lineNumber: number, whole: PatchSuggestion | null): string {
  return whole
    ? `A patch over the bad sections of line ${lineNumber} would cover ${wholeShare(whole)}: press Reject for a full reflight, or enter where the patch starts and ends (metres along the line).`
    : `Line ${lineNumber} was flown in full and QC found no bad section on it, so there is nothing to patch: enter where the patch starts and ends (metres along the line), or press Reject for a full reflight.`;
}

// ------------------------------------------------------------------------------------------------- diurnal

/** `10007.0` → 100070; null for a line that is not a number. */
export function diurnalLineNumber(line: string): number | null {
  const v = Number(line);
  if (!Number.isFinite(v) || v <= 0) return null;
  return Math.round(v * 10);
}

function hms(ms: number, offsetH: number): string {
  const d = new Date(ms + offsetH * 3_600_000);
  return d.toISOString().slice(11, 19);
}

/**
 * Bad sections from the exceedance workbook rows: each exceedance row on a survey line is placed on the line status
 * of the same line number and flight (the flight number of the flight tag, dated the line's local day or the day
 * before, for a flight that crossed local midnight; dates, not day numbers, so a 1 January line finds its
 * 31 December flight), as the share of the line's logged time it covers.
 */
export function diurnalBadSections(input: {
  readonly rows: readonly WorkbookRow[];
  readonly lines: readonly DiurnalLine[];
  readonly statuses: readonly LineStatus[];
  readonly flights: readonly Flight[];
  readonly plan: readonly PlannedLine[];
  readonly offsetH: number;
}): Map<Uuid, BadSection[]> {
  const out = new Map<Uuid, BadSection[]>();
  const live = input.statuses.filter((s) => s.deleted_at === null);
  for (const r of input.rows) {
    if (r.kind !== 'exceedance' || r.start_ms === null || r.end_ms === null) continue;
    const number = diurnalLineNumber(r.line);
    const flt = flightNumberOfTag(r.flight);
    if (number === null || flt === null) continue;
    const span = input.lines.find((l) => {
      if (l.deleted_at !== null || l.line !== r.line || flightNumberOfTag(l.flight_tag) !== flt) return false;
      const s = Date.parse(l.start_at);
      const e = Date.parse(l.end_at);
      return r.start_ms! <= e + 1000 && r.end_ms! >= s - 1000;
    });
    if (!span) continue;
    const lineDate = isoDate(new Date(Date.parse(span.start_at) + input.offsetH * 3_600_000));
    const dayBefore = addIsoDays(lineDate, -1);
    const flight = input.flights.find(
      (f) => f.deleted_at === null && f.flt_num === flt && (f.date === lineDate || f.date === dayBefore),
    );
    if (!flight) continue;
    const status = live.find((s) => s.flight_id === flight.id && s.line_number === number);
    if (!status) continue;
    const original = originalOf(status, input.plan);
    if (!original) continue;
    const s = Date.parse(span.start_at);
    const e = Date.parse(span.end_at);
    const dur = e - s;
    if (!(dur > 0)) continue;
    const run = loggedRun(status, original);
    const section = sectionOfRun(run, (r.start_ms - s) / dur, (r.end_ms - s) / dur, original.length_m);
    if (!(section.to_m > section.from_m)) continue;
    // the time at each end of the section, as the share of the logged run it lies at
    const timeAt = (along: number): string | null => {
      const len = run.end_m - run.start_m;
      if (len === 0) return null;
      const share = Math.min(1, Math.max(0, (along - run.start_m) / len));
      // to the nearest second: an end the run did not clip is the exceedance's own time
      return hms(Math.round((s + share * dur) / 1000) * 1000, input.offsetH);
    };
    const list = out.get(status.id) ?? [];
    list.push({
      source: 'diurnal',
      ...section,
      label: `Diurnal ${r.source} ${hms(r.start_ms, input.offsetH)} to ${hms(r.end_ms, input.offsetH)}`,
      times: { from: timeAt(section.from_m), to: timeAt(section.to_m) },
    });
    out.set(status.id, list);
  }
  return out;
}

// ------------------------------------------------------------------------------------------------- Geosoft QC

/**
 * Bad sections from a Geosoft QC line result linked to a line status: each event of a failed contract test, placed
 * on the line as the share of the line's fiducial range it covers.
 */
export function qcBadSections(
  result: QcLineResult,
  status: LineStatus,
  plan: readonly PlannedLine[],
): BadSection[] {
  const original = originalOf(status, plan);
  if (!original || result.fid_start === null || result.fid_end === null) return [];
  const span = result.fid_end - result.fid_start;
  if (!(span > 0)) return [];
  const run = loggedRun(status, original);
  const fids: FidRun = {
    fid_start: result.fid_start,
    fid_end: result.fid_end,
    start_m: run.start_m,
    end_m: run.end_m,
  };
  const out: BadSection[] = [];
  for (const t of result.tests) {
    if (t.status !== 'fail') continue;
    for (const e of t.events) {
      const section = sectionOfRun(
        run,
        (e.fid_start - result.fid_start) / span,
        (e.fid_end - result.fid_start) / span,
        original.length_m,
      );
      if (section.to_m > section.from_m)
        out.push({
          source: 'geosoft',
          ...section,
          // R96: the exceedance's zFid start and end, duration and distance
          label: `Flight data QC ${t.label.toLowerCase()} ${qcEventText(e)}`,
          zfids: { from: fidAtAlong(fids, section.from_m), to: fidAtAlong(fids, section.to_m) },
          test_key: t.key,
        });
    }
  }
  return out;
}

/** Suggestions for every line status with bad sections, in line order. */
export function patchSuggestions(
  sections: ReadonlyMap<Uuid, readonly BadSection[]>,
  statuses: readonly LineStatus[],
  plan: readonly PlannedLine[],
): PatchSuggestion[] {
  const out: PatchSuggestion[] = [];
  for (const [id, list] of sections) {
    const status = statuses.find((s) => s.id === id && s.deleted_at === null);
    if (!status) continue;
    const s = suggestPatch(status, list, plan);
    if (s) out.push(s);
  }
  return out.sort((a, b) => a.line_number - b.line_number);
}

/** Bad sections of every line status the stored Geosoft QC results point at (Line review). */
export function qcBadSectionsByStatus(
  results: readonly QcLineResult[],
  statuses: readonly LineStatus[],
  plan: readonly PlannedLine[],
): Map<Uuid, BadSection[]> {
  const out = new Map<Uuid, BadSection[]>();
  for (const r of results) {
    if (r.deleted_at !== null || r.line_status_id === null || r.verdict !== 'fail') continue;
    const status = statuses.find((s) => s.id === r.line_status_id && s.deleted_at === null);
    if (!status) continue;
    const list = qcBadSections(r, status, plan);
    if (list.length === 0) continue;
    out.set(status.id, [...(out.get(status.id) ?? []), ...list]);
  }
  return out;
}

/** Merge bad sections from several sources (diurnal and Geosoft) per line status. */
export function mergeBadSections(
  ...maps: ReadonlyArray<ReadonlyMap<Uuid, readonly BadSection[]>>
): Map<Uuid, BadSection[]> {
  const out = new Map<Uuid, BadSection[]>();
  for (const m of maps) for (const [k, v] of m) out.set(k, [...(out.get(k) ?? []), ...v]);
  return out;
}

// ------------------------------------------------------------------------------------------------- patch ends (R23)

/**
 * Where a Geosoft QC result's ZFIDs lie on its line (R23): the result's first and last ZFID at the logged start and
 * end of the run (`loggedRun`), as `qcBadSections` places them. Null without a ZFID range.
 */
export function qcFidRun(
  result: Pick<QcLineResult, 'fid_start' | 'fid_end'>,
  status: Pick<LineStatus, 'flown_m' | 'direction_deg' | 'start_along_m' | 'end_along_m'>,
  original: PlannedLine,
): FidRun | null {
  if (result.fid_start === null || result.fid_end === null || !(result.fid_end > result.fid_start))
    return null;
  const run = loggedRun(status, original);
  return { fid_start: result.fid_start, fid_end: result.fid_end, start_m: run.start_m, end_m: run.end_m };
}

/** One end of a patch: metres along the original line, its easting and northing, and its ZFID when it was flown. */
export interface PatchEnd {
  readonly along_m: number;
  readonly x: number;
  readonly y: number;
  /** Null where the patch end lies beyond the flown run, or the line has no Geosoft QC run. */
  readonly zfid: number | null;
}

/** A suggestion as the nav system will fly it (R23). */
export interface PatchEnds {
  /** The next version of the reviewed line (100070 → 100071), as Line review numbers the patch or reflight. */
  readonly number: number;
  readonly start: PatchEnd;
  readonly end: PatchEnd;
  readonly length_m: number;
  /** The `.lns` row, in the line's planned direction. */
  readonly row: LnsRow;
}

/**
 * The ends of a suggested patch (or of the whole line for a suggested reflight) with their ZFIDs (R23). The ZFID of an
 * end is placed by distance along the flown run between the line's first and last ZFID in the latest Geosoft QC result
 * for this line status; none where the end was not flown.
 */
export function patchEnds(
  s: PatchSuggestion,
  status: LineStatus,
  plan: readonly PlannedLine[],
  results: readonly QcLineResult[],
): PatchEnds {
  const original = originalOf(status, plan);
  if (!original) throw new RangeError(`Line ${status.line_number} has no original line in the plan.`);
  const seg: Segment = s.kind === 'reflight' ? { from_m: 0, to_m: original.length_m } : s.patch;
  const number = status.line_number + 1;
  const row = patchRow(original, seg, number);
  const result = [...results]
    .filter((r) => r.deleted_at === null && r.line_status_id === status.id && r.fid_start !== null)
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
  const run = result ? qcFidRun(result, status, original) : null;
  const end = (along: number, x: number, y: number): PatchEnd => ({
    along_m: cm(along),
    x,
    y,
    zfid: run ? fidAtAlong(run, along) : null,
  });
  return {
    number,
    start: end(seg.from_m, row.x1, row.y1),
    end: end(seg.to_m, row.x2, row.y2),
    length_m: cm(seg.to_m - seg.from_m),
    row,
  };
}

/**
 * Local times (`HH:MM:SS`) at the ends of a suggested patch (R23), placed by distance along the flown run between where
 * the line starts and ends in Diurnal QC's survey-line file (the same line and flight as `diurnalBadSections` finds).
 * Null where the end was not flown, or Diurnal QC has no such line.
 */
export function diurnalPatchTimes(
  s: PatchSuggestion,
  status: LineStatus,
  plan: readonly PlannedLine[],
  lines: readonly DiurnalLine[],
  flights: readonly Flight[],
  offsetH: number,
): { readonly start: string | null; readonly end: string | null } {
  const none = { start: null, end: null };
  const original = originalOf(status, plan);
  const flight = flights.find((f) => f.id === status.flight_id);
  if (!original || !flight) return none;
  const label = `${String(status.line_number).slice(0, -1)}.${String(status.line_number).slice(-1)}`;
  const span = lines.find((l) => {
    if (l.deleted_at !== null || l.line !== label || flightNumberOfTag(l.flight_tag) !== flight.flt_num)
      return false;
    const day = isoDate(new Date(Date.parse(l.start_at) + offsetH * 3_600_000));
    return day === flight.date || addIsoDays(day, -1) === flight.date;
  });
  if (!span) return none;
  const t0 = Date.parse(span.start_at);
  const t1 = Date.parse(span.end_at);
  const run = loggedRun(status, original);
  const len = run.end_m - run.start_m;
  if (!(t1 > t0) || len === 0) return none;
  const seg: Segment = s.kind === 'reflight' ? { from_m: 0, to_m: original.length_m } : s.patch;
  const at = (along: number): string | null => {
    const share = (along - run.start_m) / len;
    if (share < -0.0001 || share > 1.0001) return null;
    return hms(t0 + Math.min(1, Math.max(0, share)) * (t1 - t0), offsetH);
  };
  return { start: at(seg.from_m), end: at(seg.to_m) };
}
